import { useEffect, useState, type ReactElement } from 'react';
import { api, type CleanupReport, type StorageView } from '../api';
import { Sheet } from './Sheet';

/**
 * How full the media volume is, and a button to tidy it now instead of
 * tonight. The rules are fixed and written out here, because a button that
 * deletes things has to say what it deletes before anyone presses it.
 */

function mb(bytes: number): string {
  if (bytes >= 1024 ** 3) return `${(bytes / 1024 ** 3).toFixed(2)} GB`;
  if (bytes >= 1024 ** 2) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${Math.round(bytes / 1024)} KB`;
}

function describe(report: CleanupReport): string {
  const parts: string[] = [];
  if (report.exports) parts.push(`${report.exports} Export${report.exports === 1 ? '' : 'e'}`);
  if (report.previews) parts.push(`${report.previews} Vorschau${report.previews === 1 ? '' : 'en'}`);
  if (report.work) parts.push(`${report.work} Arbeitsverzeichnis${report.work === 1 ? '' : 'se'}`);
  if (report.orphans) parts.push(`${report.orphans} verwaiste Datei${report.orphans === 1 ? '' : 'en'}`);
  if (!parts.length) return 'Nichts zu löschen.';
  return `${mb(report.freedBytes)} frei gemacht: ${parts.join(', ')}.`;
}

/**
 * The backup container writes into a volume the API can only read. What it
 * shows is the one thing that matters: is there a recent stand, or not.
 */
function BackupCard({ backups }: { backups: StorageView['backups'] }): ReactElement {
  const latest = backups.latestAt ? new Date(backups.latestAt) : null;
  const ageHours = latest ? (Date.now() - latest.getTime()) / 3600e3 : Infinity;
  const stale = ageHours > 36;
  return (
    <div className="card" style={{ margin: '0 0 14px' }} data-backup={stale ? 'stale' : 'ok'}>
      <div className="spread">
        <div>
          <div className="title">Sicherung der Datenbank</div>
          <div className="sub">
            {latest
              ? `zuletzt ${latest.toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })} · ${mb(backups.latestBytes)} · ${backups.count === 1 ? 'ein Stand' : `${backups.count} Stände`} (${mb(backups.totalBytes)})`
              : 'Noch keine Sicherung. Der Dienst „backup“ schreibt täglich eine.'}
          </div>
        </div>
        <span className={`badge ${stale ? 'err' : 'ok'}`}>{stale ? 'alt' : 'da'}</span>
      </div>
    </div>
  );
}

export function StorageSheet({ onClose }: { onClose: () => void }): ReactElement {
  const [view, setView] = useState<StorageView | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<CleanupReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try {
      setView((await api.storageUsage()).storage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Speicher konnte nicht gelesen werden.');
    }
  };
  useEffect(() => {
    void load();
  }, []);

  const cleanup = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await api.cleanupStorage();
      setResult(res.report);
      setView(res.storage);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Aufräumen fehlgeschlagen.');
    } finally {
      setBusy(false);
    }
  };

  const rows: [string, number][] = view
    ? [
        ['Originale', view.originals],
        ['Proxys und Vorschaubilder', view.proxies],
        ['Exporte', view.exports],
        ['Arbeitsverzeichnisse', view.work],
        ['Sonstiges (Logos, Schriften)', view.other],
      ]
    : [];

  return (
    <Sheet title="Speicher" onClose={onClose}>
      {error ? <p className="error-text">{error}</p> : null}

      <div className="card" style={{ margin: '0 0 14px' }}>
        <div className="spread">
          <div>
            <div className="title">{view ? `${mb(view.total)} belegt` : 'Wird gezählt …'}</div>
            <div className="sub">
              {view
                ? `${view.files} Dateien${view.freeBytes !== null ? ` · noch ${mb(view.freeBytes)} frei` : ''}`
                : ''}
            </div>
          </div>
        </div>
      </div>

      {view ? (
        <div className="list storage-rows">
          {rows.map(([label, bytes]) => (
            <div className="row" key={label}>
              <div className="grow">
                <div className="title">{label}</div>
              </div>
              <span className="sub">{mb(bytes)}</span>
            </div>
          ))}
        </div>
      ) : null}

      {view?.backups.configured ? <BackupCard backups={view.backups} /> : null}

      {/* Die Datenbank neben dem Laufwerk. „Räumt die Software auch auf? Oder
          wird sie immer größer“ (Sascha, 7. September) -- die Antwort war für
          das Laufwerk ja und für die Datenbank nein, und sie stand nirgends.
          Jetzt steht sie hier, mit Zahlen statt mit Zusicherungen. */}
      {view?.datenbank.length ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>
            Datenbank · {mb(view.datenbankBytes)}
          </h3>
          <div className="list storage-rows" data-datenbank>
            {view.datenbank.slice(0, 10).map((t) => (
              <div className="row" key={t.tabelle} data-tabelle={t.tabelle} data-art={t.art}>
                <div className="grow">
                  <div className="title">
                    {t.tabelle}
                    {t.art === 'waechst' ? <span className="badge" style={{ marginLeft: 8 }}>wächst</span> : null}
                    {t.art === 'geraeumt' ? <span className="badge ok" style={{ marginLeft: 8 }}>wird geräumt</span> : null}
                  </div>
                  {t.was ? <div className="sub">{t.was}</div> : null}
                </div>
                <span className="sub" style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                  {t.zeilen.toLocaleString('de-DE')}
                  <br />
                  {mb(t.bytes)}
                </span>
              </div>
            ))}
          </div>
          <p className="hint">
            Das Schnitt-Protokoll wächst mit jedem Schritt und wird <b>nicht</b> geräumt: daran hängt der Weg zurück im
            Verlauf, und der braucht es lückenlos vom ersten Schritt an. Wenn es zu groß wird, ist das eine
            Entscheidung — kein Aufräumer sollte sie nachts von selbst treffen.
          </p>
        </>
      ) : null}

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Aufräumen</h3>
      <p className="hint" style={{ marginTop: 0 }}>
        {view?.scheduled ? 'Läuft täglich von selbst. ' : 'Der Zeitplan ist abgeschaltet; nur dieser Knopf räumt auf. '}
        Weg kommen: Exporte nach {view?.retentionDays ?? 14} Tagen in Box oder aus archivierten Projekten ·
        Vorschauen nach zwei Tagen · liegengebliebene Arbeitsverzeichnisse · Dateien ohne Projekt · abgelaufene
        Einladungen · Fehlerprotokolle älter als 60 Tage. Originale, Proxys und das Schnitt-Protokoll bleiben.
      </p>
      {view?.lastCleanup ? (
        <p className="hint">
          Zuletzt {new Date(view.lastCleanup.at).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}:{' '}
          {describe(view.lastCleanup)}
        </p>
      ) : null}
      <button style={{ width: '100%' }} onClick={() => void cleanup()} disabled={busy || !view}>
        {busy ? 'Räumt auf …' : 'Jetzt aufräumen'}
      </button>
      {result ? (
        <p className={result.errors.length ? 'error-text' : 'hint'} data-cleanup-result>
          {describe(result)}
          {result.errors.length ? ` Nicht geklappt: ${result.errors.join(' · ')}` : ''}
        </p>
      ) : null}
    </Sheet>
  );
}
